import { useTranslation } from "react-i18next";
import { InvoiceDto } from "@/types/api/api-types";
import { createColumnHelper } from "@tanstack/react-table";
import Pdf from "@/assets/icons/pdf-2.svg?react";
import { InvoicePaymentStatusBadge } from "@/components/shared/badge/invoicePaymentStatus.badge";
import { Dispatch, SetStateAction } from "react";
import { Pen, Table, FileCog, FileUp, Trash, X, Check } from "lucide-react";

const columnHelper = createColumnHelper<InvoiceDto>();

const STATUS_EDIT_ROLES = ["SUPERADMIN", "ACCOUNTANT"];

export const useInvoiceTableColumns = (
  displayDocument: (invoiceKey: string) => Promise<void>,
  role: string | null,
  setSelectedInvoice: Dispatch<SetStateAction<InvoiceDto | null>>,
  setInvoiceDeleteModal: Dispatch<SetStateAction<string | null>>,
  setInvoiceToEdit: Dispatch<SetStateAction<InvoiceDto | null>>,
) => {
  const { t } = useTranslation("boInvoices");
  return [
    columnHelper.display({
      id: "client",
      header: t("invoicesTable.columns.client"),
      cell: (info) => (
        <div className="max-w-[300px] truncate">
          {info.row.original.billingEntity.company.businessName}
        </div>
      ),
    }),
    columnHelper.display({
      id: "invoice",
      header: t("invoicesTable.columns.invoice"),
      cell: (info) => (
        <div className="flex flex-row items-center gap-4">
          {info.row.original.type === "AUTO" ? (
            <FileCog width={20} height={20} />
          ) : (
            <FileUp width={20} height={20} />
          )}
          <span className="max-w-[400px] truncate text-sm font-medium text-gray-900">
            {t("invoicesTable.invoiceName", {
              invoiceId: info.row.original.invoiceId,
              companyName: info.row.original.billingEntity.companyName,
            })}
          </span>
        </div>
      ),
    }),
    columnHelper.display({
      id: "amount",
      header: t("invoicesTable.columns.amount"),
      cell: (info) => (
        <div className="flex flex-row items-center gap-4">
          <span className="text-sm font-medium text-gray-900">
            {(info.row.original.amountToBill / 100).toLocaleString("fr-FR", {
              style: "currency",
              currency: "EUR",
            })}
          </span>
        </div>
      ),
    }),
    columnHelper.display({
      id: "rib",
      header: t("invoicesTable.columns.rib"),
      cell: (info) => {
        if (!info.row.original.rib) return "";
        return `${info.row.original.rib.shortenName}`;
      },
    }),
    columnHelper.display({
      id: "updatedBy",
      header: t("invoicesTable.columns.updatedBy"),
      cell: (info) => {
        if (!info.row.original.updatedBy) return "";
        return `${info.row.original.updatedBy?.firstName.slice(0, 1).toUpperCase()}${info.row.original.updatedBy?.lastName.slice(0, 1).toUpperCase()}`;
      },
    }),
    columnHelper.display({
      id: "status",
      header: t("invoicesTable.columns.status"),
      meta: `justify-center`,
      cell: (info) => <InvoicePaymentStatusBadge status={info.row.original.paymentStatus} />,
    }),
    columnHelper.display({
      id: "display",
      header: "",
      cell: (info) => (
        <div className="grid w-[75px] grid-cols-2 items-center justify-center gap-3">
          <button
            className="h-fit w-fit rounded-full p-1 hover:bg-gray-200"
            onClick={() => displayDocument(info.row.original.invoiceKey)}
          >
            <Table width={20} height={20} color="#06F" />
          </button>
          {info.row.original.invoicePDFKey ? (
            <button
              className="h-fit w-fit rounded-full p-1 hover:bg-gray-200"
              onClick={() => displayDocument(info.row.original.invoicePDFKey!)}
            >
              <Pdf width={20} height={20} color="#06F" />
            </button>
          ) : (
            <div />
          )}
        </div>
      ),
    }),
    columnHelper.display({
      id: "actions",
      header: "",
      cell: (info) => (
        <div className="grid w-[100px] grid-cols-3 items-center justify-center gap-3">
          {info.row.original.paymentStatus !== "PAID" &&
          role &&
          STATUS_EDIT_ROLES.includes(role) ? (
            <button
              className="h-fit w-fit rounded-full p-1 hover:bg-gray-200"
              onClick={() => setSelectedInvoice(info.row.original)}
            >
              {info.row.original.paymentStatus !== "PAID" ? (
                <Check width={20} height={20} color="#06F" />
              ) : (
                <X width={20} height={20} color="#ff0000" />
              )}
            </button>
          ) : (
            <div className="h-6 w-6" />
          )}
          {info.row.original.paymentStatus !== "PAID" ? (
            <button
              className="h-fit w-fit rounded-full p-1 hover:bg-gray-200"
              onClick={() => setInvoiceToEdit(info.row.original)}
            >
              <Pen width={20} height={20} color="#06F" />
            </button>
          ) : (
            <div className="h-6 w-6" />
          )}
          {role && STATUS_EDIT_ROLES.includes(role) ? (
            <button
              className="h-fit w-fit rounded-full p-1 hover:bg-gray-200"
              onClick={() => setInvoiceDeleteModal(info.row.original.id)}
            >
              <Trash width={20} height={20} color="#ff0000" />
            </button>
          ) : (
            <div className="h-6 w-6" />
          )}
        </div>
      ),
    }),
  ];
};
